/**
 * Per-company feature flags ("funcții noi în testare") — client side.
 *
 * Contract: docs/handovers/2026-09-24-partener/release-audit-2026-09-30/
 * company-features/CONTRACT.md (backend repo). Every new feature is switched
 * on PER COMPANY by the Autocare super administrator; the auth payload
 * (`verify_token` / login) carries `company_features: string[]`, the keys that
 * are effectively on for the user's company (for `isa`: every key available
 * globally).
 *
 * The client mirrors the backend middleware `company-feature:<key>` so that a
 * company without a feature never sees its menu entries, routes or controls.
 * These features are NEW, so an older backend that does not send
 * `company_features` at all fails closed (feature hidden).
 */

export const COMPANY_FEATURE_KEYS = [
  "driver_disruptions",
  "driver_cash_handover",
  "partner_disruptions",
  "reports_analytics",
  "settlements",
  "booking_operations",
  "promotions_pricing",
  "notification_self_service",
] as const;

export type CompanyFeatureKey = typeof COMPANY_FEATURE_KEYS[number];

export const isCompanyFeatureKey = (value: unknown): value is CompanyFeatureKey =>
  typeof value === "string" && (COMPANY_FEATURE_KEYS as readonly string[]).includes(value);

// The existing key type also defines the exhaustive RO/RU/EN copy catalog.
// Ana uses the backend's Romanian catalog text through its existing fallback,
// while access checks and API refusals must still recognise its feature key.
export const COMPANY_FEATURE_ACCESS_KEYS = [...COMPANY_FEATURE_KEYS, "partner_chat"] as const;
export type CompanyFeatureAccessKey = typeof COMPANY_FEATURE_ACCESS_KEYS[number];
export const isCompanyFeatureAccessKey = (value: unknown): value is CompanyFeatureAccessKey =>
  typeof value === "string" && (COMPANY_FEATURE_ACCESS_KEYS as readonly string[]).includes(value);

export type CompanyFeatureUser = {isa?: unknown; company_features?: unknown; company_id?: unknown} | null | undefined;

/**
 * Whether the user's company has `key` switched on.
 *  - super administrator (`isa`, boolean or 1/0 from the API) → always;
 *  - otherwise the key must be in `company_features`;
 *  - field absent / not an array (older backend) → false (fail closed).
 */
export function hasCompanyFeature(user: CompanyFeatureUser, key: CompanyFeatureAccessKey): boolean {
  if (!user) return false;
  if (user.isa) return true;
  return Array.isArray(user.company_features) && user.company_features.includes(key);
}

// Routes owned by a company feature. First match wins.
const routeCompanyFeatures: Array<[RegExp, CompanyFeatureAccessKey]> = [
  [/^\/ana(?:\/|$)/, "partner_chat"],
  [/^\/settlements(?:\/|$)/, "settlements"],
  [/^\/analytics(?:\/|$)/, "reports_analytics"],
  [/^\/promotions(?:\/|$)/, "promotions_pricing"],
  [/^\/settings\/message-templates$/, "booking_operations"],
  [/^\/manifest-schedule$/, "booking_operations"],
];

const normalizePath = (path: string): string => (path.length > 1 ? path.replace(/\/+$/, "") : path);

/** The company feature a partner route needs, or `null` when the route is not feature-gated. */
export function companyFeatureForPartnerRoute(path: string): CompanyFeatureAccessKey | null {
  const normalized = normalizePath(path || "");
  return routeCompanyFeatures.find(([pattern]) => pattern.test(normalized))?.[1] ?? null;
}

/** Router guard + aside menu: a feature-gated route needs the feature; any other route passes. */
export function canEnterCompanyFeatureRoute(user: CompanyFeatureUser, path: string): boolean {
  const feature = companyFeatureForPartnerRoute(path);
  // Auth's company_id is the user's own company, including for isa users.
  // Autocare uses its existing chat channels and cannot activate a partner one.
  if (feature === "partner_chat" && Number(user?.company_id) === 1) return false;
  return feature === null || hasCompanyFeature(user, feature);
}

export const COMPANY_FEATURE_DISABLED_ERROR = "company_feature_disabled";

export interface CompanyFeatureRefusal {
  feature: CompanyFeatureAccessKey | null;
}

type Rec = Record<string, unknown>;
const isRecord = (value: unknown): value is Rec => typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The standard refusal of the `company-feature:<key>` middleware
 * (HTTP 403 `{ok:false, error:"company_feature_disabled", feature, message}`)
 * carried by an axios-like error, or `null` for anything else.
 */
export function parseCompanyFeatureRefusal(error: unknown): CompanyFeatureRefusal | null {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  if (!response) return null;
  let body = response.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (_) {
      return null;
    }
  }
  if (!isRecord(body) || body.error !== COMPANY_FEATURE_DISABLED_ERROR) return null;
  return {feature: isCompanyFeatureAccessKey(body.feature) ? body.feature : null};
}
